/**
 * Every level the app teaches, in order. A student is placed by the diagnostic, not by grade (Meliesa, Oct 29): the
 * teacher sets a starting lesson, and the levels open in order from there. Tigard Times lives inside Level 3's
 * lessons and is taught as is for every student who starts there, whatever their grade.
 */
import type { Lesson } from "./types";
import { LEVELK, K_POST } from "./levelK";
import { LEVEL1, L1_POST } from "./level1";
import { LEVEL2, L2_POST } from "./level2";
import { LEVEL3 } from "./level3";
import { L3_POST } from "./level3/checkups";
import { LEVEL4 } from "./level4";
import { L4_POST } from "./level4/checkups";
import { LEVEL5, L5_POST } from "./level5";
import { LEVEL6, L6_PRE, L6_POST } from "./level6";
import { LEVEL7, L7_PRE, L7_POST } from "./level7";
import { LEVEL8, L8_PRE, L8_POST } from "./level8";
import { applyTensStrand } from "./tensStrand";

export interface Level {
  id: string;
  /** What students see: always "Level N" — never a grade (Meliesa: a 5th grader in Level 3 shouldn't see "3rd grade"). */
  name: string;
  /** The grade the level matches. Internal / teacher-only — never shown to students. */
  book: string;
  lessons: Lesson[];
  pre?: Lesson;
  post?: Lesson;
  /** The beginning check-up is the previous level's end check-up (its items point at that level's lessons). */
  preFrom?: string;
}

/** Meliesa, Oct 29: each level's End Check-Up is the next level's Beginning Check-Up — one sitting counts for both,
 *  and its misses point at the earlier level's lessons (what to teach before this level). */
const chained = (lvl: string, from: Lesson, prev = `Level ${Number(lvl) - 1}`): Lesson => ({ id: `L${lvl}.pre`, level: lvl, num: 0, title: "Beginning Check-Up", blurb: `the ${prev} End Check-Up`,
  steps: [{ ...from.steps[0]!, intro: `This is the ${prev} End Check-Up — it shows what to teach from ${prev} before Level ${lvl}. If you've already taken it, your results are here.` } as Lesson["steps"][number]] });

export const LEVELS: Level[] = ([
  { id: "K", name: "Level K", book: "Kindergarten", lessons: LEVELK, post: K_POST },
  { id: "1", name: "Level 1", book: "1st grade", lessons: LEVEL1, pre: chained("1", K_POST, "Level K"), post: L1_POST, preFrom: "K" },
  { id: "2", name: "Level 2", book: "2nd grade", lessons: LEVEL2, pre: chained("2", L1_POST), post: L2_POST, preFrom: "1" },
  { id: "3", name: "Level 3", book: "3rd grade", lessons: LEVEL3, pre: chained("3", L2_POST), post: L3_POST, preFrom: "2" },
  { id: "4", name: "Level 4", book: "4th grade", lessons: LEVEL4, pre: chained("4", L3_POST), post: L4_POST, preFrom: "3" },
  { id: "5", name: "Level 5", book: "5th grade", lessons: LEVEL5, pre: chained("5", L4_POST), post: L5_POST, preFrom: "4" },
  { id: "6", name: "Level 6", book: "6th grade", lessons: LEVEL6, pre: L6_PRE, post: L6_POST, preFrom: "5" },
  { id: "7", name: "Level 7", book: "7th grade", lessons: LEVEL7, pre: L7_PRE, post: L7_POST, preFrom: "6" },
  { id: "8", name: "Level 8", book: "8th grade (Pre-Algebra)", lessons: LEVEL8, pre: L8_PRE, post: L8_POST, preFrom: "7" },
] as Level[]).filter((l) => l.lessons.length > 0);

/** Every lesson and check-up, in teaching order. */
export const ALL_LESSONS: Lesson[] = LEVELS.flatMap((l) => [...(l.pre ? [l.pre] : []), ...l.lessons, ...(l.post ? [l.post] : [])]);
export const TEACHING: Lesson[] = LEVELS.flatMap((l) => l.lessons);
/** "It's all tens": each return to the tens gets its notice-it question (lessons/tensStrand.ts). */
applyTensStrand(TEACHING);
export const anyLesson = (id: string): Lesson | undefined => ALL_LESSONS.find((l) => l.id === id);
export const levelOf = (id: string): Level | undefined => LEVELS.find((v) => v.lessons.some((l) => l.id === id) || v.pre?.id === id || v.post?.id === id);
/** Short label: "Level 3 · Lesson 8". */
/** The lesson's number as the book prints it ("Review 1" for an unnumbered review). */
export const lessonNo = (l: Lesson): string => l.tag ?? String(l.num);
/** "Lesson 7", or the tag for an unnumbered review. */
/** A tag that starts with a number ("14a", a lesson added between book lessons) still reads "Lesson 14a". */
export const lessonName = (l: Lesson): string => (l.tag ? (/^\d/.test(l.tag) ? `Lesson ${l.tag}` : l.tag) : `Lesson ${l.num}`);
export const lessonLabel = (l: Lesson): string => `Level ${l.level} · ${lessonName(l)}`;
